import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBranding, assetUrl } from "@/lib/branding";
import { api, num, fmtDate, formatApiError, inr } from "@/lib/api";
import {
  LayoutDashboard, Users, Target, FileText, Package, ClipboardList, ShoppingCart, Bus, Store,
  Factory, Boxes, Truck, Receipt, ReceiptText, Wallet, BarChart3, LogOut, Menu, ChevronsLeft,
  Settings as SettingsIcon, BellRing, Bell, CalendarClock, Phone, AlertTriangle, FlaskConical, Calculator,
  ChevronLeft, ChevronRight, Sparkles, LifeBuoy, BookImage, Ruler, Warehouse, Inbox, HardDrive, HandCoins
} from "lucide-react";
import { OfflineBanner } from "@/components/OfflineBanner";
import { InstallBanner } from "@/components/InstallBanner";
import { AiAssistant } from "@/components/AiAssistant";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { playNotifySound, isSoundMuted, setSoundMuted } from "@/lib/notifySound";
import { Volume2, VolumeX } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { IntroSplash } from "@/components/IntroSplash";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

const nav = [
  { to: "/", label: "Dashboard", sub: "Overview & Analytics", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", sub: "Client Management", icon: Users, testid: "nav-customers" },
  { to: "/vendors", label: "Vendors", sub: "Supplier Accounts", icon: Store, testid: "nav-vendors" },
  { to: "/products", label: "Products", sub: "Product Master", icon: Package, testid: "nav-products" },
  { to: "/leads", label: "Leads", sub: "Enquiries & Follow-ups", icon: Target, testid: "nav-leads" },
  { to: "/quotations", label: "Quotations", sub: "Sales Quotes", icon: FileText, testid: "nav-quotations" },
  { to: "/proformas", label: "Pro Forma", sub: "Advance Invoices", icon: ReceiptText, testid: "nav-proformas" },
  { to: "/orders", label: "Sales Orders", sub: "Order Book", icon: ClipboardList, testid: "nav-orders" },
  { to: "/purchase-orders", label: "Purchase Orders", sub: "Procurement", icon: ShoppingCart, testid: "nav-purchase-orders" },
  { to: "/production", label: "Production", sub: "Job Cards & Floor", icon: Factory, testid: "nav-production" },
  { to: "/invoices", label: "Invoices", sub: "Billing & GST", icon: Receipt, testid: "nav-invoices" },
  { to: "/test-certificates", label: "Test Certificates", sub: "Quality Documents", icon: FlaskConical, testid: "nav-test-certificates" },
  { group: "Logistics", sub: "Shipping & Carriers", icon: Truck, testid: "nav-logistics", children: [
    { to: "/dispatch", label: "Dispatch & E-Way", sub: "Shipments & E-Way", icon: Truck, testid: "nav-dispatch" },
    { to: "/transporters", label: "Transporters", sub: "Carrier Master", icon: Bus, testid: "nav-transporters" },
  ] },
  { group: "Payments", sub: "Receipts & Ledger", icon: Wallet, testid: "nav-payments", children: [
    { to: "/payments", label: "Receivables", sub: "Customer Payments", icon: Wallet, testid: "nav-receivables" },
    { to: "/payments/payables", label: "Payables", sub: "Vendor Payments", icon: HandCoins, testid: "nav-payables" },
  ] },
  { to: "/reports", label: "Reports", sub: "Business Insights", icon: BarChart3, testid: "nav-reports" },
];

const PAGE_META = [
  ["/customers", "Customers", "Client Management"],
  ["/vendors", "Vendors", "Supplier Accounts"],
  ["/products", "Products", "Product Master"],
  ["/catalogue", "Catalogue", "Product Showcase"],
  ["/leads", "Leads", "Enquiries & Follow-ups"],
  ["/quotations", "Quotations", "Sales Quotes"],
  ["/proformas", "Pro Forma", "Advance Invoices"],
  ["/orders", "Sales Orders", "Order Book"],
  ["/purchase-orders", "Purchase Orders", "Procurement"],
  ["/production", "Production", "Job Cards & Floor"],
  ["/consignments", "Consignments", "Consignment Stock"],
  ["/invoices", "Invoices", "Billing & GST"],
  ["/test-certificates", "Test Certificates", "Quality Documents"],
  ["/dispatch", "Dispatch & E-Way", "Shipments & E-Way"],
  ["/transporters", "Transporters", "Carrier Master"],
  ["/payments/payables", "Payables", "Vendor Payments"],
  ["/payments", "Receivables", "Customer Payments"],
  ["/reports", "Reports", "Business Insights"],
  ["/settings", "Settings", "Preferences & Branding"],
  ["/notifications", "Notifications", "Alerts & Updates"],
  ["/size-intelligence", "Size Intelligence", "Buying Patterns"],
  ["/inventory", "Inventory", "Stock On Hand"],
  ["/rate-finder", "Rate Finder", "Price History"],
  ["/support-tickets", "Support Tickets", "Client Issues"],
  ["/disk", "Disk", "Invoice Archive"],
  ["/sync-center", "Sync Center", "Offline Queue"],
  ["/files", "Files & Media", "Documents"],
];

export const THEMES = {
  wave: { name: "Wave Light", sidebar: "#FFFFFF", page: "#F4F6FB", border: "#EAEDF3" },
  brand: { name: "Hi-Tech Brand", sidebar: "#13264D", page: "#F9F9F9", border: "rgba(255,255,255,0.10)", darkSidebar: true, accent: "#FC5F5F" },
  white: { name: "Classic White", sidebar: "#FFFFFF", page: "#F8FAFC", border: "#E2E8F0" },
  a: { name: "Warm Ivory Luxe", sidebar: "#F3EEE2", page: "#FAF7F0", border: "#E5DDCB" },
  b: { name: "Cool Mist Industrial", sidebar: "#E9EFF5", page: "#F3F6FA", border: "#D6E0EA" },
  c: { name: "Navy Tint Monochrome", sidebar: "#DFE8F3", page: "#EEF3F9", border: "#C8D6E8" },
  d: { name: "Two-Tone Bold", sidebar: "#F2F4F7", page: "#F6F8FA", border: "#E2E7EC", darkHeader: true },
  e: { name: "Charcoal Slate", sidebar: "#ECEAE6", page: "#F6F5F2", border: "#DDDAD4" },
  sand: { name: "Desert Sand", sidebar: "#EDE4D3", page: "#FAF6EE", border: "#DFD3BC" },
  lavender: { name: "Soft Lavender", sidebar: "#ECEAF4", page: "#F6F5FA", border: "#DCD8EA" },
  midnight: { name: "Midnight (Original)", sidebar: "#0B132B", page: "#F8FAFC", border: "rgba(255,255,255,0.1)", darkSidebar: true, accent: "#B0895D" },
  royal: { name: "Onyx Black", sidebar: "#0A0A0C", page: "#F6F6F7", border: "rgba(255,255,255,0.09)", darkSidebar: true, accent: "#C9A96A" },
  forest: { name: "Pastel Breeze", sidebar: "#E8F0EE", page: "#F4F9F7", border: "#D3E2DE" },
  graphite: { name: "Graphite", sidebar: "#22272E", page: "#F7F8F9", border: "rgba(255,255,255,0.1)", darkSidebar: true, accent: "#9AA7B4" },
};

function HeaderIcon({ tip, testid, children, badge, badgeColor = "bg-amber-500" }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="relative inline-flex">
          {children}
          {badge > 0 && (
            <span className={`absolute -top-1 -right-1 min-w-[15px] h-[15px] px-0.5 rounded-full ${badgeColor} text-white text-[9px] font-bold flex items-center justify-center pointer-events-none`}>
              {badge}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">{tip}</TooltipContent>
    </Tooltip>
  );
}

const iconBtn = "p-2 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors duration-200";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(() => sessionStorage.getItem("hts_intro") === "1");
  const closeIntro = () => { sessionStorage.removeItem("hts_intro"); setShowIntro(false); };
  const [collapsed, setCollapsed] = useState(localStorage.getItem("crm_sidebar_collapsed") === "1");
  const [openGroups, setOpenGroups] = useState({});
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem("crm_sidebar_collapsed", c ? "0" : "1");
      return !c;
    });
  };
  const [alerts, setAlerts] = useState(null);

  const themeParam = new URLSearchParams(location.search).get("theme");
  if (themeParam && THEMES[themeParam]) localStorage.setItem("crm_theme", themeParam);
  const stored = localStorage.getItem("crm_theme");
  const [themeKey, setThemeKey] = useState(stored && stored !== "brand" ? stored : "wave");
  useEffect(() => {
    const h = () => setThemeKey(localStorage.getItem("crm_theme") || "wave");
    window.addEventListener("crm-theme", h);
    return () => window.removeEventListener("crm-theme", h);
  }, []);
  const t = THEMES[themeParam && THEMES[themeParam] ? themeParam : themeKey] || THEMES.wave;

  const loadAlerts = () => api.get("/dashboard").then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => { loadAlerts(); }, [location.pathname]);
  useEffect(() => {
    const t = setInterval(loadAlerts, 30000);
    return () => clearInterval(t);
  }, []);

  const [soundMuted, setSoundMutedState] = useState(isSoundMuted());
  const toggleSound = () => { setSoundMuted(!soundMuted); setSoundMutedState(!soundMuted); };
  const prevAlertTotal = useRef(null);
  useEffect(() => {
    if (!alerts) return;
    const total = (alerts.followups_due || []).length + (alerts.pending_batches || []).length + (alerts.portal_requests || []).length + (alerts.delivered_alerts || []).length;
    if (prevAlertTotal.current !== null && total > prevAlertTotal.current) playNotifySound();
    prevAlertTotal.current = total;
  }, [alerts]);

  const convertReq = async (e, leadId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const r = await api.post(`/quotations/from-lead/${leadId}`);
      toast.success(`Quotation ${r.data.number} created`);
      loadAlerts();
      navigate("/quotations");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const snooze = async (leadId, days) => {
    try {
      await api.post(`/leads/${leadId}/snooze`, null, { params: { days } });
      toast.success(`Snoozed ${days} day${days > 1 ? "s" : ""}`);
      loadAlerts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const companyName = branding?.company_name || "Hi-Tech Silico";
  const companyInitials = companyName.split(/[\s-]+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const sidebarLogo = assetUrl(branding?.sidebar_logo_url);
  const sidebarLogoDark = assetUrl(branding?.sidebar_logo_dark_url);
  const collapsedLogo = assetUrl(branding?.sidebar_logo_collapsed_url);
  const fu = alerts?.followups_due || [];
  const deliveredAlerts = alerts?.delivered_alerts || [];
  const batches = alerts?.pending_batches || [];
  const portalReqs = alerts?.portal_requests || [];
  const recent = alerts?.recent_activities || [];
  const prod = alerts?.production;
  const dark = t.darkSidebar;

  const meta = PAGE_META.find(([p]) => location.pathname === p || (p !== "/" && location.pathname.startsWith(p)));
  const pageTitle = location.pathname === "/" ? "Dashboard" : (meta?.[1] || companyName);
  const pageSub = location.pathname === "/" ? "Overview & Analytics" : (meta?.[2] || "Manufacturing CRM");

  return (
    <div className="min-h-screen flex grain" style={{ background: t.page }}>
      {showIntro && <IntroSplash onDone={closeIntro} />}
      {/* Sidebar */}
      <aside
        className={`fixed lg:relative inset-y-0 left-0 z-40 w-[264px] ${collapsed ? "lg:w-[80px]" : "lg:w-[264px]"} border-r flex flex-col transform ${
          dark ? "text-slate-300" : "text-slate-600"
        } ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-all duration-200`}
        style={{ background: t.sidebar, borderColor: t.border }}
        data-testid="sidebar"
      >
        <div className={`h-[74px] border-b flex items-center justify-between flex-none ${collapsed ? "lg:px-3 px-5" : "px-5"}`} style={{ borderColor: t.border }}>
          <Link to="/" className="min-w-0 flex-1 flex items-center gap-3" data-testid="sidebar-logo">
            {collapsed ? (
              <span className="hidden lg:flex w-full justify-center">
                {collapsedLogo ? (
                  <img src={collapsedLogo} alt={companyName} className="h-10 w-10 object-contain rounded-xl" />
                ) : (
                  <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center text-sm font-display font-extrabold text-white shadow-[0_6px_18px_rgba(59,130,246,0.35)]">
                    {companyInitials}
                  </span>
                )}
              </span>
            ) : null}
            <span className={`flex items-center gap-3 min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
              {(dark && sidebarLogoDark) || sidebarLogo ? (
                <img src={(dark && sidebarLogoDark) || sidebarLogo} alt={companyName} className="h-[58px] max-w-[208px] object-contain" />
              ) : (
                <>
                  <span className="h-10 w-10 flex-none rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center text-sm font-display font-extrabold text-white shadow-[0_6px_18px_rgba(59,130,246,0.35)]">
                    {companyInitials}
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-[15px] font-display font-bold leading-tight truncate ${dark ? "text-white" : "text-slate-900"}`}>{companyName}</span>
                    <span className={`block text-[11px] truncate ${dark ? "text-slate-400" : "text-slate-400"}`}>Manufacturing CRM</span>
                  </span>
                </>
              )}
            </span>
          </Link>
          <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)} aria-label="close">
            <ChevronsLeft size={18} />
          </button>
        </div>
        <button
          onClick={toggleCollapsed}
          aria-label={collapsed ? "expand menu" : "collapse menu"}
          title={collapsed ? "Expand menu" : "Collapse menu"}
          data-testid="sidebar-collapse-btn"
          className="hidden lg:flex absolute -right-3.5 top-[88px] z-50 w-7 h-7 p-0 flex-none leading-none rounded-full items-center justify-center bg-white text-[#3B82F6] border border-slate-200 shadow-md hover:shadow-lg hover:scale-110 active:scale-95 transition-[transform,box-shadow] duration-200"
        >
          {collapsed ? <ChevronRight size={14} strokeWidth={2.5} className="relative left-[0.5px]" /> : <ChevronLeft size={14} strokeWidth={2.5} className="relative -left-[0.5px]" />}
        </button>
        <nav className="flex-1 overflow-y-auto py-3 px-3 scroll-thin">
          {nav.map((item) => {
            const renderLink = ({ to, label, sub, icon: Icon, testid }, indent = false) => (
              <NavLink key={to} to={to} end={to === "/" || to === "/payments"} data-testid={testid} onClick={() => setOpen(false)} title={label}
                className={({ isActive }) =>
                  `group flex items-center gap-3 my-1 rounded-xl transition-all duration-200 ${
                    collapsed ? "lg:justify-center lg:px-0 lg:py-2.5 px-3.5 py-2" : `px-3.5 py-2 ${indent ? "ml-3" : ""}`
                  } ${
                    isActive
                      ? "bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white shadow-[0_6px_18px_rgba(59,130,246,0.35)]"
                      : (dark ? "text-slate-300/75 hover:bg-white/[0.06] hover:text-white" : "text-slate-500 hover:bg-slate-100/80 hover:text-slate-800")
                  }`
                }>
                {({ isActive }) => (
                  <>
                    <Icon size={17} strokeWidth={isActive ? 2 : 1.6} className="flex-none transition-transform duration-200 group-hover:scale-110" />
                    <span className={`min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
                      <span className={`block text-[13px] leading-tight truncate ${isActive ? "font-semibold text-white" : `font-medium ${dark ? "" : "text-slate-700"}`}`}>{label}</span>
                      <span className={`block text-[10.5px] leading-tight truncate ${isActive ? "text-white/70" : "text-slate-400"}`}>{sub}</span>
                    </span>
                  </>
                )}
              </NavLink>
            );
            if (item.children) {
              const groupActive = item.children.some((c) => location.pathname.startsWith(c.to));
              const isOpen = openGroups[item.group] || groupActive;
              return (
                <div key={item.group}>
                  {!collapsed && (
                    <button onClick={() => setOpenGroups((g) => ({ ...g, [item.group]: !isOpen }))} data-testid={item.testid}
                      className={`w-full flex items-center gap-3 px-3.5 py-2 my-1 rounded-xl transition-colors duration-200 ${
                        groupActive
                          ? (dark ? "text-white font-semibold" : "text-slate-900 font-semibold")
                          : (dark ? "text-slate-300/75 hover:bg-white/[0.06] hover:text-white" : "text-slate-500 hover:bg-slate-100/80 hover:text-slate-800")
                      }`}>
                      <item.icon size={17} strokeWidth={1.6} className="flex-none" />
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block text-[13px] font-medium leading-tight">{item.group}</span>
                        <span className="block text-[10.5px] text-slate-400 leading-tight">{item.sub}</span>
                      </span>
                      <ChevronRight size={13} className={`transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
                    </button>
                  )}
                  {(isOpen || collapsed) && item.children.map((c) => renderLink(c, true))}
                </div>
              );
            }
            return renderLink(item);
          })}
        </nav>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-xl border-b border-slate-200/80 bg-white/80 h-[74px] flex items-center gap-3 px-4 lg:px-6">
          <button
            className="lg:hidden mr-1 text-slate-700"
            onClick={() => setOpen(true)}
            aria-label="menu"
            data-testid="menu-btn"
          >
            <Menu size={20} />
          </button>
          <div className="min-w-0 flex-none max-w-[42vw] sm:max-w-none" data-testid="header-brand">
            <div className="flex items-center gap-2">
              <h1 className="text-[17px] font-display font-bold text-slate-900 leading-tight truncate">{pageTitle}</h1>
              <span className="hidden sm:inline-flex text-[9px] font-bold uppercase tracking-wider text-[#3B82F6] bg-[#3B82F6]/10 rounded-full px-2 py-0.5 items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-emerald-500 inline-block" /> Live
              </span>
            </div>
            <p className="text-[11px] text-slate-400 leading-tight truncate">{pageSub}</p>
          </div>

          <div className="flex-1 min-w-0" />
          <GlobalSearch />

          <TooltipProvider delayDuration={150}>
            <div className="flex items-center gap-0.5">
              {/* Notifications bell — recent activity */}
              <Popover>
                <HeaderIcon tip="Notifications — recent activity">
                  <PopoverTrigger asChild>
                    <button className={`${iconBtn} relative`} data-testid="hdr-bell">
                      <Bell size={16} strokeWidth={1.5} />
                      {recent.length > 0 && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#3B82F6] pointer-events-none" />}
                    </button>
                  </PopoverTrigger>
                </HeaderIcon>
                <PopoverContent align="end" className="w-96 p-0 bg-white/90 backdrop-blur-xl backdrop-saturate-150 border-white/50 shadow-[0_16px_48px_rgba(15,23,42,0.12)]">
                  <div className="px-3 py-2.5 border-b border-slate-100 text-xs font-bold uppercase tracking-widest text-[#3B82F6] flex items-center gap-1.5"><Bell size={12} /> Recent Activity</div>
                  <div className="max-h-72 overflow-y-auto scroll-thin p-2 space-y-1">
                    {recent.slice(0, 8).map((a) => (
                      <div key={a.id} className="flex gap-2.5 text-xs px-2 py-1.5 rounded-md hover:bg-slate-50" data-testid={`bell-activity-${a.id}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-1.5 flex-none" />
                        <span className="min-w-0">
                          <span className="block text-slate-700 truncate">{a.text}</span>
                          <span className="block text-[10px] text-slate-400 mt-0.5">{a.user_name || a.user_email} · {fmtDate(a.created_at)}</span>
                        </span>
                      </div>
                    ))}
                    {recent.length === 0 && <div className="text-xs text-slate-500 text-center py-6">No recent activity.</div>}
                  </div>
                  <Link to="/notifications" className="block text-center text-[11px] font-semibold text-[#3B82F6] hover:text-[#2563EB] border-t border-slate-100 py-2 transition-colors duration-200" data-testid="bell-view-all">
                    View all notifications →
                  </Link>
                </PopoverContent>
              </Popover>

              {/* Follow-ups */}
              <Popover>
                <HeaderIcon tip="Follow-ups Due" badge={fu.length + deliveredAlerts.length} badgeColor="bg-amber-500">
                  <PopoverTrigger asChild>
                    <button className={iconBtn} data-testid="hdr-followups"><BellRing size={16} strokeWidth={1.5} /></button>
                  </PopoverTrigger>
                </HeaderIcon>
                <PopoverContent align="end" className="w-96 p-0 bg-white/90 backdrop-blur-xl backdrop-saturate-150 border-white/50 shadow-[0_16px_48px_rgba(15,23,42,0.12)]">
                  <div className="px-3 py-2.5 border-b border-slate-100 text-xs font-bold uppercase tracking-widest text-amber-700 flex items-center gap-1.5"><BellRing size={12} /> Follow-ups Due</div>
                  <div className="max-h-72 overflow-y-auto scroll-thin p-2 space-y-1.5">
                    {fu.map((f) => (
                      <div key={f.id} className={`text-xs border rounded-md px-2.5 py-2 flex items-center justify-between gap-2 ${f.overdue ? "border-rose-200 bg-rose-50/50" : "border-amber-200 bg-amber-50/50"}`} data-testid={`followup-${f.id}`}>
                        <Link to="/leads" className={`min-w-0 ${f.overdue ? "text-rose-700" : "text-amber-800"} hover:underline`}>
                          <span className="font-semibold flex items-center gap-1">{f.overdue && <AlertTriangle size={10} />}{f.company_name || f.contact_person}</span>
                          <span className="text-slate-500 flex items-center gap-1 mt-0.5">{f.mobile && <><Phone size={9} /> {f.mobile} · </>}{fmtDate(f.follow_up_date)}</span>
                        </Link>
                        <span className="flex items-center gap-1 flex-none">
                          {[1, 3, 7].map((d) => (
                            <button key={d} onClick={() => snooze(f.id, d)} title={`Snooze ${d} day(s)`}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600 hover:bg-amber-100 hover:text-amber-800 transition-colors duration-200"
                                    data-testid={`snooze-${f.id}-${d}`}>+{d}d</button>
                          ))}
                        </span>
                      </div>
                    ))}
                    {fu.length === 0 && <div className="text-xs text-slate-500 text-center py-6">No follow-ups due. All caught up!</div>}
                  </div>
                  {deliveredAlerts.length > 0 && (
                    <>
                      <div className="px-3 py-2 border-t border-slate-100 text-xs font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1.5"><Truck size={12} /> Auto-Delivered</div>
                      <div className="max-h-40 overflow-y-auto scroll-thin p-2 pt-0 space-y-1.5">
                        {deliveredAlerts.map((a) => (
                          <Link key={a.id} to="/dispatch" className="block text-xs border border-emerald-200 bg-emerald-50/60 rounded-md px-2.5 py-2 text-emerald-800 hover:bg-emerald-100/60 transition-colors duration-200" data-testid={`delivered-alert-${a.id}`}>
                            <span className="font-semibold">{a.text}</span>
                            <span className="block text-[10px] text-emerald-600 mt-0.5">{fmtDate(a.created_at)}</span>
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                  <Link to="/notifications" className="block text-center text-[11px] font-semibold text-[#3B82F6] hover:text-[#2563EB] border-t border-slate-100 py-2 transition-colors duration-200" data-testid="fu-view-all-notifications">
                    View all notifications →
                  </Link>
                </PopoverContent>
              </Popover>

              {/* Disk */}
              <span className="hidden lg:flex items-center gap-0.5">
              <HeaderIcon tip="Disk — monthly invoice folders">
                <button onClick={() => navigate("/disk")} className={iconBtn} data-testid="hdr-disk">
                  <HardDrive size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>

              {/* Support Tickets */}
              <HeaderIcon tip="Support Tickets">
                <button onClick={() => navigate("/support-tickets")} className={iconBtn} data-testid="hdr-support-tickets">
                  <LifeBuoy size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>

              {/* Portal Requests */}
              <Popover>
                <HeaderIcon tip="Client Portal Requests" badge={portalReqs.length} badgeColor="bg-[#FC5F5F]">
                  <PopoverTrigger asChild>
                    <button className={iconBtn} data-testid="hdr-portal-requests"><Inbox size={16} strokeWidth={1.5} /></button>
                  </PopoverTrigger>
                </HeaderIcon>
                <PopoverContent align="end" className="w-96 p-0 bg-white/90 backdrop-blur-xl backdrop-saturate-150 border-white/50 shadow-[0_16px_48px_rgba(15,23,42,0.12)]">
                  <div className="px-3 py-2.5 border-b border-slate-100 text-xs font-bold uppercase tracking-widest text-[#C94A4A] flex items-center gap-1.5"><Inbox size={12} /> Client Portal Requests</div>
                  <div className="max-h-72 overflow-y-auto scroll-thin p-2 space-y-1.5">
                    {portalReqs.map((r) => (
                      <Link key={r.id} to="/leads"
                            className="block text-xs border rounded-md px-2.5 py-2 transition-colors duration-200 border-[#FC5F5F]/25 bg-[#FC5F5F]/5 hover:bg-[#FC5F5F]/10"
                            data-testid={`portal-req-${r.id}`}>
                        <span className="font-semibold text-slate-800 flex items-center justify-between">
                          {r.company_name}
                          <span className="text-[10px] font-mono text-slate-400">{r.number}</span>
                        </span>
                        <span className="text-slate-500 flex items-center justify-between mt-0.5">
                          <span>{r.source.replace("Portal ", "")}{r.item_count ? ` · ${r.item_count} product${r.item_count > 1 ? "s" : ""}` : ""}</span>
                          <span>{r.value ? inr(r.value) : ""} · {fmtDate(r.created_at)}</span>
                        </span>
                        {r.item_count > 0 && (
                          <button onClick={(e) => convertReq(e, r.id)} data-testid={`portal-req-quote-${r.id}`}
                                  className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors duration-200">
                            <FileText size={10} /> Create Quotation
                          </button>
                        )}
                      </Link>
                    ))}
                    {portalReqs.length === 0 && <div className="text-xs text-slate-500 text-center py-6">No new portal requests.</div>}
                  </div>
                  <Link to="/notifications" className="block text-center text-[11px] font-semibold text-[#3B82F6] hover:text-[#2563EB] border-t border-slate-100 py-2 transition-colors duration-200" data-testid="pr-view-all-notifications">
                    View all notifications →
                  </Link>
                </PopoverContent>
              </Popover>

              {/* Pending Dispatch Batches */}
              <Popover>
                <HeaderIcon tip="Pending Dispatch Batches" badge={batches.length} badgeColor="bg-sky-500">
                  <PopoverTrigger asChild>
                    <button className={iconBtn} data-testid="hdr-batches"><CalendarClock size={16} strokeWidth={1.5} /></button>
                  </PopoverTrigger>
                </HeaderIcon>
                <PopoverContent align="end" className="w-96 p-0 bg-white/90 backdrop-blur-xl backdrop-saturate-150 border-white/50 shadow-[0_16px_48px_rgba(15,23,42,0.12)]">
                  <div className="px-3 py-2.5 border-b border-slate-100 text-xs font-bold uppercase tracking-widest text-sky-700 flex items-center gap-1.5"><CalendarClock size={12} /> Pending Dispatch Batches</div>
                  <div className="max-h-72 overflow-y-auto scroll-thin p-2 space-y-1.5">
                    {batches.map((b) => (
                      <Link key={b.id} to={`/invoices?so=${b.sales_order_id}&sched=${b.id}`}
                            className={`block text-xs border rounded-md px-2.5 py-2 transition-colors duration-200 ${b.overdue ? "border-rose-200 bg-rose-50/50 text-rose-700 hover:bg-rose-50" : "border-sky-200 bg-sky-50/50 text-sky-800 hover:bg-sky-50"}`}
                            title="Create invoice for this batch" data-testid={`batch-${b.id}`}>
                        <span className="font-semibold flex items-center gap-1">{b.overdue && <AlertTriangle size={10} />}<span className="font-mono">{b.order_number}</span> · {b.customer}</span>
                        <span className="text-slate-500 mt-0.5 block">{fmtDate(b.planned_date)} · Qty <b className="tabular-nums">{num(b.planned_qty)}</b>{b.po_number ? ` · ${b.po_number}` : ""}</span>
                      </Link>
                    ))}
                    {batches.length === 0 && <div className="text-xs text-slate-500 text-center py-6">No pending batches.</div>}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Production Floor */}
              <Popover>
                <HeaderIcon tip="Production Floor" badge={(prod?.pending || 0) + (prod?.in_progress || 0)} badgeColor="bg-indigo-500">
                  <PopoverTrigger asChild>
                    <button className={iconBtn} data-testid="hdr-production"><Factory size={16} strokeWidth={1.5} /></button>
                  </PopoverTrigger>
                </HeaderIcon>
                <PopoverContent align="end" className="w-72 p-0 bg-white/90 backdrop-blur-xl backdrop-saturate-150 border-white/50 shadow-[0_16px_48px_rgba(15,23,42,0.12)]">
                  <div className="px-3 py-2.5 border-b border-slate-100 text-xs font-bold uppercase tracking-widest text-slate-600 flex items-center gap-1.5"><Factory size={12} /> Production Floor</div>
                  {prod ? (
                    <div className="p-3 grid grid-cols-2 gap-2 text-xs">
                      <div className="border border-amber-200 bg-amber-50 rounded-md px-2.5 py-2 text-amber-800"><b className="text-base tabular-nums block">{prod.pending}</b> Pending</div>
                      <div className="border border-sky-200 bg-sky-50 rounded-md px-2.5 py-2 text-sky-800"><b className="text-base tabular-nums block">{prod.in_progress}</b> In Progress</div>
                      {prod.qa > 0 && <div className="border border-indigo-200 bg-indigo-50 rounded-md px-2.5 py-2 text-indigo-800"><b className="text-base tabular-nums block">{prod.qa}</b> QA</div>}
                      {prod.on_hold > 0 && <div className="border border-rose-200 bg-rose-50 rounded-md px-2.5 py-2 text-rose-800"><b className="text-base tabular-nums block">{prod.on_hold}</b> On Hold</div>}
                      <div className="border border-emerald-200 bg-emerald-50 rounded-md px-2.5 py-2 text-emerald-800"><b className="text-base tabular-nums block">{prod.completed_week}</b> Done this week</div>
                    </div>
                  ) : <div className="text-xs text-slate-500 text-center py-6">Loading…</div>}
                  <Link to="/production" className="block text-center text-xs text-slate-600 hover:text-slate-900 border-t border-slate-100 py-2 hover:bg-slate-50 transition-colors duration-200" data-testid="hdr-production-open">Open Production →</Link>
                </PopoverContent>
              </Popover>

              {/* Notification sound toggle */}
              <HeaderIcon tip={soundMuted ? "Unmute notification sounds" : "Mute notification sounds"}>
                <button onClick={toggleSound} className={iconBtn} data-testid="hdr-sound-toggle">
                  {soundMuted ? <VolumeX size={16} strokeWidth={1.5} /> : <Volume2 size={16} strokeWidth={1.5} />}
                </button>
              </HeaderIcon>

              {/* AI Assistant */}
              <HeaderIcon tip="AI Assistant">
                <button onClick={() => setAiOpen(true)} className={iconBtn} data-testid="hdr-ai">
                  <Sparkles size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>

              {/* Catalogue */}
              <HeaderIcon tip="Catalogue">
                <button onClick={() => navigate("/catalogue")} className={iconBtn} data-testid="hdr-catalogue">
                  <BookImage size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>

              {/* Size Intelligence */}
              <HeaderIcon tip="Size Intelligence">
                <button onClick={() => navigate("/size-intelligence")} className={iconBtn} data-testid="hdr-size-intel">
                  <Ruler size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>

              {/* Inventory */}
              <HeaderIcon tip="Inventory">
                <button onClick={() => navigate("/inventory")} className={iconBtn} data-testid="hdr-inventory">
                  <Warehouse size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>

              {/* Consignments */}
              <HeaderIcon tip="Consignments">
                <button onClick={() => navigate("/consignments")} className={iconBtn} data-testid="hdr-consignments">
                  <Boxes size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>

              {/* Rate Finder */}
              <HeaderIcon tip="Rate Finder">
                <button onClick={() => navigate("/rate-finder")} className={iconBtn} data-testid="hdr-rate-finder">
                  <Calculator size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>
              </span>

              <div className="hidden sm:block w-px h-5 mx-1.5 bg-slate-200" />

              {/* Profile / Settings / Logout */}
              <HeaderIcon tip={`${user?.name || "Profile"} · ${user?.role || ""}`}>
                <Link to="/settings" className="inline-flex" data-testid="profile-link">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold bg-gradient-to-br from-[#3B82F6] to-[#2563EB] text-white shadow-[0_4px_12px_rgba(59,130,246,0.35)] hover:scale-105 transition-transform duration-200" data-testid="user-avatar">
                    {initials}
                  </span>
                </Link>
              </HeaderIcon>
              <HeaderIcon tip="Settings">
                <button onClick={() => navigate("/settings")} className={`${iconBtn} hidden sm:inline-flex`} data-testid="settings-btn">
                  <SettingsIcon size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>
              <HeaderIcon tip="Sign out">
                <button onClick={doLogout} className={`${iconBtn} hover:text-rose-600`} data-testid="logout-btn">
                  <LogOut size={16} strokeWidth={1.5} />
                </button>
              </HeaderIcon>
            </div>
          </TooltipProvider>
        </header>
        <OfflineBanner />
        <InstallBanner />
        <main className="flex-1 p-4 lg:p-6 max-w-[1600px] w-full mx-auto">
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname}
                        initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        exit={{ opacity: 0, y: -14, filter: "blur(4px)" }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      <Toaster richColors position="top-right" />
      <AiAssistant open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
